import type { ExerciseTemplate } from '@yuan-math/exercises';
import { fraction } from '@yuan-math/math';
import {
  choiceExercise,
  distinctTraps,
  fractionExercise,
  fractionTrap,
  numericExercise,
  options,
  template,
  trap,
  within,
} from './authoring.ts';

/**
 * Arc length, surface area, work and centre of mass.
 *
 * Every template here is built from a *slice*, and every trap is a different
 * slice. That is what makes this unit worth generating exercises for at all: the
 * wrong answers are not arithmetic slips, they are the student having added up
 * the wrong small quantity, and each of them is reachable by a coherent piece of
 * reasoning.
 *
 * The families are chosen so that the integrals actually close. Arc length uses
 * y = c·x^{3/2}, where 1 + (f′)² is linear, and straight lines, where it is
 * constant; surface area uses the sphere, where the y in the radius cancels the
 * y in ds, and the cone, where ds is constant. Those are essentially the only
 * elementary cases, which is a fact about the mathematics rather than a
 * limitation of the bank — and the lesson says so.
 *
 * `arc-length-straight-line` looks too easy for a university item and is the
 * most diagnostic thing in the file. The answer is b√(1 + m²), and the two
 * standard errors give b·m and b(1 + m) — three numbers a student can compare
 * against a picture, on a figure whose length they can also get from Pythagoras.
 * An item where the right answer is independently checkable is where a
 * misconception is cheapest to correct.
 *
 * `pumping-work` reports kilojoules rather than joules. The honest figure for a
 * domestic-sized tank runs to seven digits, and a seven-digit answer box turns a
 * modelling question into a transcription exercise.
 *
 * `centroid-y-power` traps both of the two things ȳ is confused with — the
 * unhalved moment and the average value of f — because they are different
 * mistakes with the same smell, and a student who gets one back needs to be told
 * which one they made.
 */

/** `3x`, `x` — a coefficient in front of something, with 1 dropped. */
const coeff = (value: number, body: string): string => (value === 1 ? body : `${value}${body}`);

/** ∫√(a² − x²) dx evaluated at x, for the surface trap that leaves out ds. */
const semicircleArea = (r: number, x: number): number =>
  (x * Math.sqrt(r * r - x * x)) / 2 + ((r * r) / 2) * Math.asin(x / r);

export const INTEGRAL_APPLICATION_TEMPLATES: ExerciseTemplate[] = [
  /* ---------------------------------------------------------- arc length */
  template({
    id: 'arc-length-three-halves',
    conceptId: 'arc-length',
    description: 'Arc length of a curve built so that 1 + (f′)² is linear.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const c = rng.int(1, 3);
      const b = rng.int(1, 8);
      const answer = (2 / (3 * c * c)) * ((1 + c * c * b) ** 1.5 - 1);
      const oneOmitted = ((2 * c) / 3) * b ** 1.5;
      const rootDistributed = b + ((2 * c) / 3) * b ** 1.5;

      return numericExercise({
        prompt: `Find the length of the curve from x = 0 to x = ${b}. Give your answer to three decimal places.`,
        latex: `y = \\frac{${2 * c}}{3}x^{3/2}`,
        value: answer,
        tolerance: within(0.001, answer),
        summary: `f′(x) = ${c === 1 ? '' : c}√x, so 1 + (f′)² = 1 + ${c === 1 ? '' : `${c * c}`}x — which is why this curve was chosen. Integrating its square root from 0 to ${b} gives ${answer.toFixed(3)}.`,
        hint: 'Differentiate, square, add 1. The root should collapse to something elementary.',
        steps: [
          { label: 'Differentiate.', latex: `\\frac{dy}{dx} = ${c === 1 ? '' : c}x^{1/2}` },
          { label: 'Square it and add 1.', latex: `1 + \\left(\\frac{dy}{dx}\\right)^{2} = 1 + ${c * c === 1 ? '' : c * c}x` },
          { label: 'Set up the arc-length integral.', latex: `L = \\int_{0}^{${b}}\\sqrt{1 + ${c * c === 1 ? '' : c * c}x}\\;dx` },
          { label: `Substitute u = 1 + ${c * c === 1 ? '' : c * c}x.`, latex: `L = \\frac{2}{3 \\cdot ${c * c}}\\Big[(1 + ${c * c === 1 ? '' : c * c}x)^{3/2}\\Big]_{0}^{${b}}` },
          { label: 'Evaluate.', latex: `L = ${answer.toFixed(3)}` },
        ],
        traps: [
          trap(oneOmitted.toFixed(3), 'arc-length-one-omitted'),
          trap(rootDistributed.toFixed(3), 'arc-length-root-distributed'),
        ],
      });
    },
  }),

  template({
    id: 'arc-length-straight-line',
    conceptId: 'arc-length',
    description: 'Arc length of a straight line, where the answer is also a hypotenuse.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const m = rng.int(1, 8);
      const b = rng.int(1, 9);
      const k = rng.int(-6, 6);
      const answer = b * Math.sqrt(1 + m * m);
      const oneOmitted = b * m;
      const rootDistributed = b * (1 + m);

      return numericExercise({
        prompt: `Find the length of this line from x = 0 to x = ${b}. Give your answer to three decimal places.`,
        latex: `y = ${coeff(m, 'x')}${k === 0 ? '' : k < 0 ? ` - ${-k}` : ` + ${k}`}`,
        value: answer,
        tolerance: within(0.001, answer),
        summary: `The derivative is the constant ${m}, so the integrand is the constant √(1 + ${m * m}) and the length is ${b}√${1 + m * m} = ${answer.toFixed(3)}. Pythagoras says the same thing: the segment runs ${b} across and ${b * m} up.`,
        hint: 'The derivative of a line is constant, so the arc-length integrand is constant too.',
        steps: [
          { label: 'Differentiate.', latex: `\\frac{dy}{dx} = ${m}` },
          { label: 'Build the integrand.', latex: `\\sqrt{1 + ${m}^{2}} = \\sqrt{${1 + m * m}}` },
          { label: 'A constant integrand is a width times a height.', latex: `L = ${b}\\sqrt{${1 + m * m}}` },
          { label: 'Evaluate.', latex: `L = ${answer.toFixed(3)}` },
        ],
        traps: [
          trap(oneOmitted.toFixed(3), 'arc-length-one-omitted'),
          trap(rootDistributed.toFixed(3), 'arc-length-root-distributed'),
        ],
      });
    },
  }),

  template({
    id: 'arc-length-integrand-choice',
    conceptId: 'arc-length',
    description: 'Choose the integral that gives the length of a curve.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const n = rng.int(2, 5);
      const b = rng.int(2, 9);
      const derivative = n === 2 ? `${n}x` : `${n}x^{${n - 1}}`;

      return choiceExercise({
        prompt: `Which integral gives the length of y = x^${n} from x = 0 to x = ${b}?`,
        ...options(
          [
            { latex: `\\int_{0}^{${b}}\\sqrt{1 + (${derivative})^{2}}\\;dx`, correct: true },
            { latex: `\\int_{0}^{${b}}\\big(1 + ${derivative}\\big)\\,dx`, misconceptionId: 'arc-length-root-distributed' },
            { latex: `\\int_{0}^{${b}}\\sqrt{(${derivative})^{2}}\\;dx`, misconceptionId: 'arc-length-one-omitted' },
            { latex: `\\int_{0}^{${b}}\\sqrt{1 + (x^{${n}})^{2}}\\;dx`, misconceptionId: 'arc-length-one-omitted' },
          ],
          rng.shuffle,
        ),
        summary: `ds = √((dx)² + (dy)²), and factoring dx out gives √(1 + (dy/dx)²) dx. It is the *derivative* that is squared, not the function — and the root covers the whole of 1 + (f′)², so it cannot be taken term by term.`,
        hint: 'Start from Pythagoras on a tiny piece of curve, and divide through by dx.',
      });
    },
  }),

  /* ----------------------------------------------- surfaces of revolution */
  template({
    id: 'surface-sphere-band',
    conceptId: 'surface-of-revolution',
    description: 'Surface swept by an arc of a semicircle rotated about the x-axis.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const r = rng.int(3, 9);
      const p = rng.int(0, r - 2);
      const q = rng.int(p + 1, r - 1);
      const answer = 2 * Math.PI * r * (q - p);
      const asVolume = Math.PI * (r * r * (q - p) - (q ** 3 - p ** 3) / 3);
      const withoutArcElement = 2 * Math.PI * (semicircleArea(r, q) - semicircleArea(r, p));

      return numericExercise({
        prompt: `The arc of this curve from x = ${p} to x = ${q} is rotated about the x-axis. Find the area of the surface swept out, to three decimal places.`,
        latex: `y = \\sqrt{${r * r} - x^{2}}`,
        value: answer,
        tolerance: within(0.001, answer),
        summary: `Here y′ = −x/y, so 1 + (y′)² = (x² + y²)/y² = ${r * r}/y² and ds = (${r}/y) dx. The y cancels against the 2πy, leaving the constant 2π(${r}) to integrate across a width of ${q - p}: the answer is ${answer.toFixed(3)}.`,
        hint: 'Build 1 + (y′)² over a common denominator, and use the fact that x² + y² is constant on this curve.',
        steps: [
          { label: 'Differentiate the semicircle.', latex: `\\frac{dy}{dx} = \\frac{-x}{\\sqrt{${r * r} - x^{2}}} = \\frac{-x}{y}` },
          { label: 'Build the arc element.', latex: `\\sqrt{1 + \\frac{x^{2}}{y^{2}}} = \\frac{\\sqrt{x^{2}+y^{2}}}{y} = \\frac{${r}}{y}` },
          { label: 'Assemble 2πy ds; the y cancels.', latex: `2\\pi y \\cdot \\frac{${r}}{y} = 2\\pi(${r})` },
          { label: 'Integrate a constant across the width.', latex: `S = 2\\pi(${r})(${q} - ${p})` },
          { label: 'Evaluate.', latex: `S = ${answer.toFixed(3)}` },
        ],
        traps: distinctTraps([
          trap(asVolume.toFixed(3), 'surface-computed-as-volume'),
          trap(withoutArcElement.toFixed(3), 'surface-arc-element-omitted'),
        ]),
      });
    },
  }),

  template({
    id: 'surface-cone',
    conceptId: 'surface-of-revolution',
    description: 'Surface swept by a straight line rotated about the x-axis — a cone.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const m = rng.int(1, 5);
      const b = rng.int(1, 6);
      const slantFactor = Math.sqrt(1 + m * m);
      const answer = Math.PI * m * b * b * slantFactor;
      const asVolume = (Math.PI * m * m * b ** 3) / 3;
      const withoutArcElement = Math.PI * m * b * b;

      return numericExercise({
        prompt: `The segment from x = 0 to x = ${b} is rotated about the x-axis. Find the curved surface area, to three decimal places.`,
        latex: `y = ${coeff(m, 'x')}`,
        value: answer,
        tolerance: within(0.001, answer),
        summary: `The derivative is ${m}, so ds = √${1 + m * m} dx and the surface is ∫2π(${coeff(m, 'x')})√${1 + m * m} dx from 0 to ${b}, which is π(${m})(${b})²√${1 + m * m} = ${answer.toFixed(3)}. That is πrl for a cone of radius ${m * b} and slant ${(b * slantFactor).toFixed(3)}.`,
        hint: 'The radius of the circle a point sweeps is its y, and ds is constant because the curve is straight.',
        steps: [
          { label: 'Differentiate.', latex: `\\frac{dy}{dx} = ${m}` },
          { label: 'The arc element is constant.', latex: `ds = \\sqrt{1 + ${m * m}}\\;dx` },
          { label: 'Assemble the surface integral.', latex: `S = \\int_{0}^{${b}} 2\\pi(${coeff(m, 'x')})\\sqrt{${1 + m * m}}\\;dx` },
          { label: 'Integrate.', latex: `S = 2\\pi(${m})\\sqrt{${1 + m * m}}\\cdot\\frac{${b}^{2}}{2}` },
          { label: 'Evaluate.', latex: `S = ${answer.toFixed(3)}` },
        ],
        traps: distinctTraps([
          trap(asVolume.toFixed(3), 'surface-computed-as-volume'),
          trap(withoutArcElement.toFixed(3), 'surface-arc-element-omitted'),
        ]),
      });
    },
  }),

  template({
    id: 'surface-element-choice',
    conceptId: 'surface-of-revolution',
    description: 'Tell the surface element from the volume element.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const b = rng.int(2, 9);
      const aboutX = rng.bool();
      const axis = aboutX ? 'x' : 'y';
      const correct = aboutX
        ? `\\int_{0}^{${b}} 2\\pi f(x)\\sqrt{1 + (f\'(x))^{2}}\\;dx`
        : `\\int_{0}^{${b}} 2\\pi x\\sqrt{1 + (f\'(x))^{2}}\\;dx`;
      const wrongRadius = aboutX
        ? `\\int_{0}^{${b}} 2\\pi x\\sqrt{1 + (f\'(x))^{2}}\\;dx`
        : `\\int_{0}^{${b}} 2\\pi f(x)\\sqrt{1 + (f\'(x))^{2}}\\;dx`;

      return choiceExercise({
        prompt: `The curve y = f(x) between x = 0 and x = ${b} is rotated about the ${axis}-axis. Which integral gives the area of the surface?`,
        ...options(
          [
            { latex: correct, correct: true },
            { latex: `\\int_{0}^{${b}} \\pi\\big(f(x)\\big)^{2}\\,dx`, misconceptionId: 'surface-computed-as-volume' },
            { latex: `\\int_{0}^{${b}} 2\\pi f(x)\\,dx`, misconceptionId: 'surface-arc-element-omitted' },
            { latex: wrongRadius, misconceptionId: 'surface-computed-as-volume' },
          ],
          rng.shuffle,
        ),
        summary: `The element is 2π × (distance to the axis) × ds. About the ${axis}-axis that distance is ${aboutX ? 'y, which is f(x)' : 'x'}, and ds always carries the square root. πf(x)² dx is a disc, and a stack of discs is a volume.`,
        hint: 'Ask what circle a point on the curve sweeps out, and how wide the band it makes is.',
      });
    },
  }),

  /* --------------------------------------------------------------- work */
  template({
    id: 'spring-work',
    conceptId: 'work-integrals',
    description: 'Work done stretching a spring between two extensions.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      /*
       * Everything is generated in tenths of a metre and divided at the last
       * moment. Adding 0.1 to 0.2 in binary gives 0.30000000000000004, and a
       * prompt that prints that has told the student the answer is generated.
       */
      const referenceTenths = rng.pick([1, 2, 5]);
      const force = rng.int(1, 12) * 10;
      const k = (force * 10) / referenceTenths;
      const fromTenths = rng.int(1, 4);
      const toTenths = fromTenths + rng.int(1, 5);
      const reference = referenceTenths / 10;
      const from = fromTenths / 10;
      const to = toTenths / 10;
      const answer = (k * (toTenths * toTenths - fromTenths * fromTenths)) / 200;
      const forceTimesDistance = (force * (toTenths - fromTenths)) / 10;
      const fromNaturalLength = (k * toTenths * toTenths) / 200;

      return numericExercise({
        prompt: `A force of ${force} N holds a spring ${reference} m beyond its natural length. How much work stretches it from ${from} m to ${to} m beyond its natural length? Give your answer to two decimal places.`,
        unit: 'J',
        value: answer,
        tolerance: within(0.01, answer),
        summary: `Hooke's law gives k = ${force}/${reference} = ${k} N/m. The work is ∫kx dx from ${from} to ${to}, which is ½(${k})(${to}² − ${from}²) = ${answer.toFixed(2)} J.`,
        hint: 'Find k from the data first. Then remember the integral runs between the two extensions, not from zero.',
        steps: [
          { label: 'Hooke\'s law, from the measurement given.', latex: `${force} = k(${reference}) \\quad\\Longrightarrow\\quad k = ${k}` },
          { label: 'Set up the work integral between the two extensions.', latex: `W = \\int_{${from}}^{${to}} ${k}x\\,dx` },
          { label: 'Integrate.', latex: `W = \\frac{${k}}{2}\\Big[x^{2}\\Big]_{${from}}^{${to}}` },
          { label: 'Evaluate.', latex: `W = ${answer.toFixed(2)}` },
        ],
        traps: distinctTraps([
          trap(forceTimesDistance.toFixed(2), 'work-force-taken-as-constant'),
          trap(fromNaturalLength.toFixed(2), 'spring-extension-measured-wrongly'),
        ]),
      });
    },
  }),

  template({
    id: 'pumping-work',
    conceptId: 'work-integrals',
    description: 'Work done emptying a rectangular tank over its rim.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const length = rng.int(1, 5);
      const width = rng.int(1, 5);
      const depth = rng.int(3, 6);
      // 9.8 kN per cubic metre, so the answer is in kilojoules and stays legible.
      const answer = 4.9 * length * width * depth * depth;
      const liftedFullDepth = 2 * answer;
      const weightOnly = 9.8 * length * width * depth;

      return numericExercise({
        prompt: `A tank ${length} m by ${width} m in plan and ${depth} m deep is full of water, which weighs 9.8 kN per cubic metre. How much work is needed to pump all of it out over the top? Give your answer in kilojoules, to one decimal place.`,
        unit: 'kJ',
        value: answer,
        tolerance: within(0.1, answer),
        summary: `A slab at depth y has volume ${length * width} dy and weight 9.8 × ${length * width} dy, and must be lifted a distance y. So W = ∫ ${9.8 * length * width}y dy from 0 to ${depth}, which is ${answer.toFixed(1)} kJ.`,
        hint: 'Take a horizontal slab at depth y. How much does it weigh, and how far does it have to travel?',
        steps: [
          { label: 'Weight of a slab at depth y.', latex: `dF = 9.8 \\times ${length * width}\\,dy` },
          { label: 'It is lifted a distance y to reach the rim.', latex: `dW = ${9.8 * length * width}\\,y\\,dy` },
          { label: 'Add up every slab.', latex: `W = \\int_{0}^{${depth}} ${9.8 * length * width}\\,y\\,dy` },
          { label: 'Integrate.', latex: `W = ${9.8 * length * width}\\cdot\\frac{${depth}^{2}}{2}` },
          { label: 'Evaluate.', latex: `W = ${answer.toFixed(1)}` },
        ],
        traps: [
          trap(liftedFullDepth.toFixed(1), 'pumping-distance-mismeasured'),
          trap(weightOnly.toFixed(1), 'work-force-taken-as-constant'),
        ],
      });
    },
  }),

  template({
    id: 'work-slice-choice',
    conceptId: 'work-integrals',
    description: 'Choose the work integral for a tank pumped over its rim.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const area = rng.int(2, 9);
      const depth = rng.int(2, 8);
      const density = 9800;

      return choiceExercise({
        prompt: `A tank of constant cross-section ${area} m² and depth ${depth} m is full of water weighing ${density} N/m³, measured with y as the depth below the rim. Which integral gives the work needed to empty it over the rim?`,
        ...options(
          [
            { latex: `\\int_{0}^{${depth}} ${density * area}\\,y\\,dy`, correct: true },
            { latex: `\\int_{0}^{${depth}} ${density * area}(${depth} - y)\\,dy`, misconceptionId: 'pumping-distance-mismeasured' },
            { latex: `\\int_{0}^{${depth}} ${density * area}\\,dy`, misconceptionId: 'work-force-taken-as-constant' },
            { latex: `${density * area * depth} \\times ${depth}`, misconceptionId: 'work-force-taken-as-constant' },
          ],
          rng.shuffle,
        ),
        summary: `A slab at depth y weighs ${density} × ${area} dy and travels y to reach the rim, so dW = ${density * area} y dy. Using ${depth} − y would be the distance from the *base*, which is the journey no slab makes.`,
        hint: 'Write down the weight of one slab, then how far that particular slab has to move.',
      });
    },
  }),

  /* ------------------------------------------------------- centre of mass */
  template({
    id: 'centroid-x-power',
    conceptId: 'center-of-mass',
    description: 'The horizontal coordinate of the centroid of a region under a power curve.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const n = rng.int(1, 5);
      const b = rng.int(1, 4);
      const answer = fraction(b * (n + 1), n + 2);
      const momentOnly = fraction(b ** (n + 2), n + 2);

      return fractionExercise({
        prompt: `Find the x-coordinate of the centroid of the region under this curve between x = 0 and x = ${b}. Give your answer as a fraction.`,
        latex: `y = x^{${n}}`,
        value: answer,
        summary: `The area is ${b}^${n + 1}/${n + 1} and the moment about the y-axis is ∫x·x^${n} dx = ${b}^${n + 2}/${n + 2}. Dividing gives x̄ = ${b}(${n + 1})/${n + 2} = ${answer.n}/${answer.d}.`,
        hint: 'Centre of mass is moment divided by area, and both integrals run over the same interval.',
        steps: [
          { label: 'The area — the denominator of the average.', latex: `A = \\int_{0}^{${b}} x^{${n}}\\,dx = \\frac{${b}^{${n + 1}}}{${n + 1}}` },
          { label: 'The moment about the y-axis.', latex: `M_{y} = \\int_{0}^{${b}} x\\cdot x^{${n}}\\,dx = \\frac{${b}^{${n + 2}}}{${n + 2}}` },
          { label: 'Divide.', latex: `\\bar{x} = \\frac{${answer.n}}{${answer.d}}` },
        ],
        traps: [fractionTrap(momentOnly, 'centroid-not-divided-by-area')],
      });
    },
  }),

  template({
    id: 'centroid-y-power',
    conceptId: 'center-of-mass',
    description: 'The vertical coordinate of the centroid, where the half is the whole point.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const n = rng.int(1, 5);
      const b = rng.int(1, 4);
      const answer = fraction((n + 1) * b ** n, 2 * (2 * n + 1));
      const halfOmitted = fraction((n + 1) * b ** n, 2 * n + 1);
      const averageHeight = fraction(b ** n, n + 1);

      return fractionExercise({
        prompt: `Find the y-coordinate of the centroid of the region under this curve between x = 0 and x = ${b}. Give your answer as a fraction.`,
        latex: `y = x^{${n}}`,
        value: answer,
        summary: `A strip at x has mass x^${n} dx and sits at its own midpoint, height x^${n}/2, so the moment is ½∫x^${2 * n} dx = ${b}^${2 * n + 1}/${2 * (2 * n + 1)}. Dividing by the area ${b}^${n + 1}/${n + 1} gives ȳ = ${answer.n}/${answer.d}.`,
        hint: 'The strip is not concentrated at the top of the curve. Where along its own height does it act?',
        steps: [
          { label: 'The area.', latex: `A = \\frac{${b}^{${n + 1}}}{${n + 1}}` },
          { label: 'The moment about the x-axis — half the height, times the mass.', latex: `M_{x} = \\int_{0}^{${b}}\\tfrac{1}{2}\\big(x^{${n}}\\big)^{2}\\,dx = \\frac{${b}^{${2 * n + 1}}}{${2 * (2 * n + 1)}}` },
          { label: 'Divide.', latex: `\\bar{y} = \\frac{${answer.n}}{${answer.d}}` },
        ],
        traps: [
          fractionTrap(halfOmitted, 'centroid-height-not-halved'),
          fractionTrap(averageHeight, 'centroid-taken-as-average-height'),
        ],
      });
    },
  }),

  template({
    id: 'centroid-point-masses',
    conceptId: 'center-of-mass',
    description: 'The balance point of three masses on a line.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const masses = [rng.int(1, 9), rng.int(1, 9), rng.int(1, 9)];
      const positions = [rng.int(0, 3), rng.int(4, 7), rng.int(8, 12)];
      const moment = masses.reduce((sum, m, i) => sum + m * (positions[i] as number), 0);
      const total = masses.reduce((sum, m) => sum + m, 0);
      const answer = fraction(moment, total);
      const unweighted = fraction(positions.reduce((sum, x) => sum + x, 0), 3);

      return fractionExercise({
        prompt: `Masses of ${masses[0]} kg, ${masses[1]} kg and ${masses[2]} kg sit at positions ${positions[0]}, ${positions[1]} and ${positions[2]} on a line. Where is the centre of mass? Give your answer as a fraction.`,
        value: answer,
        summary: `The moment is ${masses[0]}(${positions[0]}) + ${masses[1]}(${positions[1]}) + ${masses[2]}(${positions[2]}) = ${moment}, and the total mass is ${total}. The balance point is ${moment}/${total} = ${answer.n}/${answer.d}.`,
        hint: 'Weight each position by its mass before averaging — the heavy end pulls the balance point towards it.',
        steps: [
          { label: 'Moment about the origin.', latex: `\\sum m_{i}x_{i} = ${moment}` },
          { label: 'Total mass.', latex: `\\sum m_{i} = ${total}` },
          { label: 'Divide.', latex: `\\bar{x} = \\frac{${moment}}{${total}} = \\frac{${answer.n}}{${answer.d}}` },
        ],
        traps: distinctTraps([
          fractionTrap(unweighted, 'centroid-weights-ignored'),
          fractionTrap(fraction(moment, 1), 'centroid-not-divided-by-area'),
        ]),
      });
    },
  }),
];
